class WorkoutsVideoSection extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this._sectionId = this.dataset.sectionId;
    this._isDesktop = window.matchMedia('(min-width: 1024px)');
    this._wrapper = this.querySelector('.swiper-wrapper');
    this._baseCards = [...this.querySelectorAll('.workouts-video__card')];
    this._cards = [...this._baseCards];
    this._activeIdx = 0;
    this._userPaused = false;
    this._hasStarted = false;
    this._inViewport = false;
    this._loadingTimers = new Map();
    this._loaderDelay = 400;

    this._initSwiper();
    this._initViewportObserver();
    this._initTapHandlers();
    this._initDesktopHover();
    this._initVisibilityHandler();
    this._initVideoLoaders();

    this._isDesktop.addEventListener('change', () => {
      if (this._swiperInit) this._swiperInit();
      this._initDesktopHover();
      if (!this._isDesktop.matches) {
        this._initViewportObserver();
      } else {
        if (this._observer) this._observer.disconnect();
        this._pauseAll();
      }
    });
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    if (this._swiper) this._swiper.destroy(true, true);
    document.removeEventListener('visibilitychange', this._onVisibility);
    if (this._onWindowResize) {
      window.removeEventListener('resize', this._onWindowResize);
    }
    clearTimeout(this._resizeTimer);
  }

  _initSwiper() {
    const swiperEl = this.querySelector('.workouts-video__swiper');
    if (!swiperEl) return;

    const prevBtn = this.querySelector('.workouts-video__nav--prev');
    const nextBtn = this.querySelector('.workouts-video__nav--next');

    let hasUserInteracted = false;

    const onSlideChange = swiper => {
      this._onSlideChange(swiper.realIndex);

      if (!hasUserInteracted) return;

      if (typeof calculateScrollPercentage === 'function') {
        calculateScrollPercentage(
          swiper,
          `#shopify-section-${this._sectionId} h2`
        );
      }
    };

    const mobileConfig = {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      speed: 800,
      effect: 'creative',
      creativeEffect: {
        prev: { opacity: 1, translate: [0, 0, 0] },
        next: { opacity: 1, translate: [0, 0, 0] }
      },
      watchSlidesProgress: true,
      navigation: { prevEl: prevBtn, nextEl: nextBtn },
      on: {
        touchStart: () => (hasUserInteracted = true),
        navigationNext: () => (hasUserInteracted = true),
        navigationPrev: () => (hasUserInteracted = true),
        slideChange: onSlideChange
      }
    };

    const desktopConfig = {
      slidesPerView: 'auto',
      spaceBetween: 16,
      loop: true,
      watchSlidesProgress: true,
      navigation: { prevEl: prevBtn, nextEl: nextBtn },
      on: {
        touchStart: () => (hasUserInteracted = true),
        navigationNext: () => (hasUserInteracted = true),
        navigationPrev: () => (hasUserInteracted = true),
        slideChange: onSlideChange,
        resize: () => this._updateNavVisibility()
      }
    };

    this._swiperInit = () => {
      if (typeof Swiper === 'undefined') return;
      if (this._swiper) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }
      const loop = this._shouldLoop();
      this._syncLoopSlides(loop);
      swiperEl.classList.remove('wv-anim-ready');
      const config = this._isDesktop.matches
        ? { ...desktopConfig, loop }
        : mobileConfig;
      this._swiper = new Swiper(swiperEl, config);
      this._initDesktopHover();
      this._updateNavVisibility();
      requestAnimationFrame(() => swiperEl.classList.add('wv-anim-ready'));
    };

    this._onWindowResize = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        if (!this._swiper) return;

        if (!this._isDesktop.matches) {
          this._updateNavVisibility();
          return;
        }

        const want = this._shouldLoop();
        const have = !!this._swiper.params.loop;
        const needsMoreClones =
          want && this._cards.length < this._loopSlidesNeeded();

        if (want !== have || needsMoreClones) {
          this._swiperInit();
        } else {
          this._updateNavVisibility();
        }
      }, 200);
    };
    window.addEventListener('resize', this._onWindowResize);

    if (typeof Swiper !== 'undefined') {
      requestAnimationFrame(this._swiperInit);
    } else {
      window.addEventListener(
        'load',
        () => requestAnimationFrame(this._swiperInit),
        { once: true }
      );
    }
  }

  _shouldLoop() {
    if (!this._isDesktop.matches || this._baseCards.length < 2) return false;

    const swiperEl = this.querySelector('.workouts-video__swiper');
    if (!swiperEl) return false;

    const baseTotal = this._baseCards.reduce(
      (sum, card, i) => sum + card.offsetWidth + (i > 0 ? 16 : 0),
      0
    );

    return baseTotal > swiperEl.clientWidth + 1;
  }

  _loopSlidesNeeded() {
    const swiperEl = this.querySelector('.workouts-video__swiper');
    if (!swiperEl || !this._baseCards.length) return 0;

    const slideWidth = this._baseCards[0].offsetWidth + 16;
    if (slideWidth <= 16) return 0;

    const perView = swiperEl.clientWidth / slideWidth;
    return Math.ceil(perView) * 2;
  }

  _syncLoopSlides(loop) {
    if (!this._wrapper) return;

    this._wrapper
      .querySelectorAll('.workouts-video__card.is-clone')
      .forEach(node => node.remove());

    if (loop && this._baseCards.length) {
      const needed = this._loopSlidesNeeded();
      let i = 0;
      while (this._wrapper.children.length < needed) {
        const source = this._baseCards[i % this._baseCards.length];
        const clone = source.cloneNode(true);
        clone.classList.add('is-clone');
        clone.setAttribute('aria-hidden', 'true');
        const cloneLoader = clone.querySelector('.workouts-video__loader');
        if (cloneLoader)
          cloneLoader.classList.remove('is-initial', 'is-visible');
        this._wrapper.appendChild(clone);
        i += 1;
      }
    }

    this._cards = [...this._wrapper.querySelectorAll('.workouts-video__card')];
  }

  _updateNavVisibility() {
    const nav = this.querySelector('nav');
    const swiperEl = this.querySelector('.workouts-video__swiper');
    if (!nav || !swiperEl) return;

    if (!this._isDesktop.matches) {
      nav.style.display = '';
      return;
    }

    const spaceBetween = 16;
    const totalWidth = this._cards.reduce(
      (sum, card, i) => sum + card.offsetWidth + (i > 0 ? spaceBetween : 0),
      0
    );
    const fits = totalWidth <= swiperEl.clientWidth + 1;
    nav.style.display = fits ? 'none' : '';
  }

  _initViewportObserver() {
    if (this._observer) this._observer.disconnect();

    this._observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this._inViewport = true;
            if (this._isDesktop.matches) return;
            if (!this._hasStarted) {
              this._hasStarted = true;
              this._playCard(this._activeIdx);
            } else if (!this._userPaused) {
              this._resumeCard(this._activeIdx);
            }
          } else {
            this._inViewport = false;
            if (!this._isDesktop.matches) {
              this._pauseCard(this._activeIdx, true);
            }
          }
        });
      },
      { threshold: 0.3 }
    );

    this._observer.observe(this);
  }

  _initTapHandlers() {
    this._cards.forEach(card => {
      const media = card.querySelector('.workouts-video__media');
      if (!media) return;

      media.addEventListener('click', () => {
        if (this._isDesktop.matches) return;
        const idx = parseInt(card.dataset.index, 10);
        if (idx !== this._activeIdx) return;

        if (this._userPaused) {
          this._userPaused = false;
          this._resumeCard(idx);
        } else {
          this._userPaused = true;
          this._pauseCard(idx, true);
          this._showPlayBtn(card, true);
        }
      });
    });
  }

  _initDesktopHover() {
    this._cards.forEach(card => {
      const media = card.querySelector('.workouts-video__media');
      const video = card.querySelector('.workouts-video__video');
      if (!media || !video) return;

      card.removeEventListener('mouseenter', card._hoverEnter);
      card.removeEventListener('mouseleave', card._hoverLeave);
      if (media._hoverEnter) {
        media.removeEventListener('mouseenter', media._hoverEnter);
        media.removeEventListener('mouseleave', media._hoverLeave);
      }

      media._hoverEnter = () => {
        if (!this._isDesktop.matches) return;
        video.currentTime = 0;
        video.muted = true;
        video.play().catch(() => {});
        card.classList.add('is-playing');
        if (!video._wvLoadedBound) {
          video._wvLoadedBound = true;
          video.addEventListener('canplay', () =>
            card.classList.add('is-loaded')
          );
        }
      };

      media._hoverLeave = () => {
        if (!this._isDesktop.matches) return;
        video.pause();
        video.currentTime = 0;
        card.classList.remove('is-playing');
      };

      media.addEventListener('mouseenter', media._hoverEnter);
      media.addEventListener('mouseleave', media._hoverLeave);
    });
  }

  _initVisibilityHandler() {
    this._onVisibility = () => {
      if (this._isDesktop.matches) return;
      if (document.hidden) {
        this._pauseCard(this._activeIdx, true);
      } else if (this._inViewport && !this._userPaused) {
        this._resumeCard(this._activeIdx);
      }
    };
    document.addEventListener('visibilitychange', this._onVisibility);
  }

  _playCard(idx) {
    const card = this._cards[idx];
    if (!card) return;

    const video = card.querySelector('.workouts-video__video');
    if (!video) return;

    video.muted = true;
    video.playsInline = true;

    this._setupVideoEvents(card, video, idx);
    this._showLoadingIfSlow(card, video);

    video.play().catch(() => {});
  }

  _setupVideoEvents(card, video, idx) {
    if (video._wvBound) return;
    video._wvBound = true;

    video.addEventListener('playing', () => {
      this._hideLoader(card);
      this._clearLoadingTimer(idx);
      card.classList.add('is-playing');
      this._userPaused = false;
      this._showPlayBtn(card, false);
      const segs = this._getSegs();
      if (segs && segs[idx] && segs[idx].classList.contains('is-playing')) {
        this._resumeProgress(idx);
      } else {
        this._startProgress(idx, video.duration);
      }
    });

    video.addEventListener('waiting', () => {
      if (!video._wvLoaderBound) this._showLoader(card);
      this._pauseProgress(idx);
    });

    video.addEventListener('canplay', () => {
      if (!video._wvLoaderBound) this._hideLoader(card);
      this._clearLoadingTimer(idx);
      card.classList.add('is-loaded');
    });

    video.addEventListener('ended', () => {
      this._onVideoEnded(idx);
    });
  }

  _resumeCard(idx) {
    const card = this._cards[idx];
    if (!card) return;

    const video = card.querySelector('.workouts-video__video');
    if (!video || !video.paused) return;

    video.play().catch(() => {});
    this._resumeProgress(idx);
  }

  _pauseCard(idx, rememberTime) {
    const card = this._cards[idx];
    if (!card) return;

    const video = card.querySelector('.workouts-video__video');
    if (video && !video.paused) {
      video.pause();
    }

    this._hideLoader(card);
    this._pauseProgress(idx);

    if (!rememberTime && video) {
      video.currentTime = 0;
    }
  }

  _pauseAll() {
    this._cards.forEach((_, i) => this._pauseCard(i, false));
  }

  _onSlideChange(newIdx) {
    if (this._isDesktop.matches) return;

    const prevIdx = this._activeIdx;
    this._activeIdx = newIdx;
    this._userPaused = false;

    this._pauseCard(prevIdx, false);
    this._markSegsDone(newIdx);

    if (this._inViewport) {
      this._playCard(newIdx);
    }
  }

  _onVideoEnded(idx) {
    const segs = this._getSegs();
    if (segs) {
      segs.forEach(s => s.classList.remove('is-playing', 'is-paused'));
      segs[idx] && segs[idx].classList.add('is-done');
    }

    const nextIdx = idx + 1;
    const isLast = nextIdx >= this._cards.length;

    if (isLast) {
      this._resetAllProgress();
      this._activeIdx = 0;
      if (this._swiper) this._swiper.slideTo(0, 300);
      if (this._inViewport) this._playCard(0);
    } else {
      this._activeIdx = nextIdx;
      if (this._swiper) this._swiper.slideNext(300);
      if (this._inViewport) this._playCard(nextIdx);
    }
  }

  _getSegs() {
    const wrap = this.querySelector('.workouts-video__progress-wrap');
    if (!wrap) return null;
    return [...wrap.querySelectorAll('.workouts-video__progress-seg')];
  }

  _startProgress(cardIdx, duration) {
    const segs = this._getSegs();
    if (!segs || !segs[cardIdx]) return;

    const activeSeg = segs[cardIdx];
    const fill = activeSeg.querySelector('.workouts-video__progress-fill');
    if (!fill) return;

    activeSeg.classList.remove('is-done', 'is-paused');
    activeSeg.style.setProperty(
      '--wv-duration',
      `${isFinite(duration) && duration > 0 ? duration : 8}s`
    );
    activeSeg.classList.add('is-playing');
  }

  _pauseProgress(cardIdx) {
    const segs = this._getSegs();
    if (!segs || !segs[cardIdx]) return;

    const activeSeg = segs[cardIdx];
    activeSeg.classList.add('is-paused');
  }

  _resumeProgress(cardIdx) {
    const segs = this._getSegs();
    if (!segs || !segs[cardIdx]) return;

    const activeSeg = segs[cardIdx];
    if (!activeSeg.classList.contains('is-paused')) return;
    activeSeg.classList.remove('is-paused');
  }

  _markSegsDone(upToIdx) {
    const segs = this._getSegs();
    if (!segs) return;
    segs.forEach((seg, segIdx) => {
      seg.classList.remove('is-playing', 'is-paused', 'is-done');
      if (segIdx < upToIdx) {
        seg.classList.add('is-done');
      }
    });
  }

  _resetAllProgress() {
    const segs = this._getSegs();
    if (!segs) return;
    segs.forEach(seg => {
      seg.classList.remove('is-playing', 'is-paused', 'is-done');
      const fill = seg.querySelector('.workouts-video__progress-fill');
      if (fill) fill.style.width = '';
    });
  }

  _initVideoLoaders() {
    this._cards.forEach(card => {
      const video = card.querySelector('.workouts-video__video');
      if (!video) return;

      const onReady = () => {
        card.classList.add('is-loaded');
        setTimeout(() => this._hideLoader(card), 150);
      };
      const onStall = () => {
        if (this._isDesktop.matches && card.classList.contains('is-loaded')) {
          return;
        }
        this._showLoader(card);
      };

      video.addEventListener('loadeddata', onReady);
      video.addEventListener('canplay', onReady);
      video.addEventListener('waiting', onStall);
      video.addEventListener('stalled', onStall);

      video._wvLoaderBound = true;

      if (video.readyState >= 2) {
        onReady();
        return;
      }

      video.preload = 'auto';
      video.load();
    });
  }

  _showLoader(card) {
    const loader = card.querySelector('.workouts-video__loader');
    if (loader) loader.classList.add('is-visible');
  }

  _hideLoader(card) {
    const loader = card.querySelector('.workouts-video__loader');
    if (loader) loader.classList.remove('is-visible', 'is-initial');
  }

  _showLoadingIfSlow(card, video) {
    const idx = parseInt(card.dataset.index, 10);
    this._clearLoadingTimer(idx);

    if (
      video.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA &&
      video.networkState !== HTMLMediaElement.NETWORK_IDLE
    ) {
      const timer = setTimeout(() => {
        if (
          video.paused ||
          video.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA
        ) {
          this._showLoader(card);
        }
      }, this._loaderDelay);
      this._loadingTimers.set(idx, timer);
    }
  }

  _clearLoadingTimer(idx) {
    if (this._loadingTimers.has(idx)) {
      clearTimeout(this._loadingTimers.get(idx));
      this._loadingTimers.delete(idx);
    }
  }

  _showPlayBtn(card, show) {
    const btn = card.querySelector('.workouts-video__play-btn');
    if (!btn) return;
    btn.classList.toggle('is-visible', show);
  }
}

if (!customElements.get('workouts-video-section')) {
  customElements.define('workouts-video-section', WorkoutsVideoSection);
}
